import { getPublicSiteUrl } from "@/lib/env";

export interface SEOConfig {
  title: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "product" | "profile";
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  siteName?: string;
  locale?: string;
  alternateLocales?: string[];
  noindex?: boolean;
  nofollow?: boolean;
  canonical?: string;
}

export const SEO_SITE_URL = getPublicSiteUrl();

type MetaTag =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string }
  | { rel: string; href: string }
  | { rel: string; hreflang: string; href: string };

export function seo(config: SEOConfig): MetaTag[] {
  const {
    title,
    description = "CN Apex Club - 专业的 Apex Legends 游戏社区，提供复盘服务、技巧分析和游戏工具",
    keywords = "Apex Legends,CN Apex Club,复盘,游戏技巧,战术分析,电竞",
    image = "/favicon.png",
    url,
    type = "website",
    author,
    publishedTime,
    modifiedTime,
    siteName = "CN Apex Club",
    locale = "zh_CN",
    alternateLocales = [],
    noindex = false,
    nofollow = false,
    canonical,
  } = config;
  const normalizedImage = toAbsoluteUrl(image);
  const normalizedUrl = url ? toAbsoluteUrl(url) : undefined;
  const normalizedCanonical = canonical ? toAbsoluteUrl(canonical) : undefined;

  const tags: MetaTag[] = [
    { title },
    { name: "description", content: description },
    { name: "keywords", content: keywords },
    { name: "author", content: author || "CN Apex Club" },
    {
      name: "robots",
      content: `${noindex ? "noindex" : "index"},${nofollow ? "nofollow" : "follow"}`,
    },

    // Open Graph
    { property: "og:type", content: type },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:image", content: normalizedImage },
    { property: "og:site_name", content: siteName },
    { property: "og:locale", content: locale },

    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: normalizedImage },

    // Additional meta tags
    { name: "format-detection", content: "telephone=no" },
    { name: "theme-color", content: "#ef4444" }, // Red color matching the brand
  ];

  // Add conditional tags
  if (type === "article" && publishedTime) {
    tags.push({ property: "article:published_time", content: publishedTime });
  }
  if (type === "article" && modifiedTime) {
    tags.push({ property: "article:modified_time", content: modifiedTime });
  }
  if (type === "article" && author) {
    tags.push({ property: "article:author", content: author });
  }
  if (normalizedUrl) {
    tags.push({ property: "og:url", content: normalizedUrl });
  }
  if (normalizedCanonical) {
    tags.push({ rel: "canonical", href: normalizedCanonical });
  }

  // Add alternate locales
  alternateLocales.forEach((alt) => {
    if (normalizedUrl) {
      tags.push({ rel: "alternate", hreflang: alt, href: normalizedUrl });
    }
  });

  return tags.filter((tag) => {
    if ("content" in tag) {
      return tag.content !== undefined && tag.content !== "";
    }
    if ("href" in tag) {
      return tag.href !== undefined && tag.href !== "";
    }
    return true;
  });
}

// Structured data helpers
export function generateWebsiteStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "CN Apex Club",
    url: SEO_SITE_URL,
    description: "专业的 Apex Legends 游戏社区，提供复盘服务、技巧分析和游戏工具",
  };
}

export function generateOrganizationStructuredData() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "CN Apex Club",
    url: SEO_SITE_URL,
    logo: `${SEO_SITE_URL}/favicon.png`,
    description: "专业的 Apex Legends 游戏社区",
    sameAs: [
      // Add social media links when available
    ],
  };
}

export function generateBreadcrumbStructuredData(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// Game-related structured data helpers
export function generateApexLegendsGameData() {
  return {
    "@type": "VideoGame",
    name: "Apex Legends",
    description: "Free-to-play battle royale game",
    genre: ["Battle Royale", "First-Person Shooter"],
    publisher: {
      "@type": "Organization",
      name: "Electronic Arts",
    },
    developer: {
      "@type": "Organization",
      name: "Respawn Entertainment",
    },
  };
}

export function generateWebApplicationStructuredData(config: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: config.name,
    description: config.description,
    url: config.url,
    applicationCategory: "GameApplication",
    operatingSystem: "Web Browser",
    isPartOf: {
      "@type": "WebSite",
      name: "CN Apex Club",
      url: SEO_SITE_URL,
    },
    about: generateApexLegendsGameData(),
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "CNY",
    },
  };
}

export function generateWebPageStructuredData(config: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: config.name,
    description: config.description,
    url: config.url,
    isPartOf: {
      "@type": "WebSite",
      name: "CN Apex Club",
      url: SEO_SITE_URL,
    },
    about: generateApexLegendsGameData(),
  };
}

export function toAbsoluteUrl(pathOrUrl: string, baseUrl = SEO_SITE_URL) {
  if (!pathOrUrl || pathOrUrl.length === 0) {
    return `${baseUrl}/favicon.png`;
  }

  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  if (pathOrUrl.startsWith("/")) {
    return `${baseUrl}${pathOrUrl}`;
  }

  return `${baseUrl}/${pathOrUrl}`;
}

export function generateNewsArticleStructuredData(config: {
  title: string;
  description: string;
  url: string;
  image?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  siteName?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: config.title,
    description: config.description,
    url: config.url,
    image: config.image ? [config.image] : undefined,
    datePublished: config.publishedTime,
    dateModified: config.modifiedTime ?? config.publishedTime,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": config.url,
    },
    author: {
      "@type": "Organization",
      name: config.author || "CN Apex Club",
    },
    publisher: {
      "@type": "Organization",
      name: config.siteName || "CN Apex Club",
      logo: {
        "@type": "ImageObject",
        url: `${SEO_SITE_URL}/favicon.png`,
      },
    },
  };
}

// Helper function to create structured data script
export function createStructuredDataScript(data: any) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(data),
  };
}
